import React, { useState, useEffect, useRef } from 'react';
import { WifiOff, RefreshCw } from 'lucide-react';

const OfflineNotice = () => {
  const [status, setStatus] = useState('ok'); // 'ok' | 'offline' | 'serverDown'
  const recoveryRef = useRef(null);

  useEffect(() => {
    const handleOffline = () => setStatus('offline');
    const handleOnline = () => setStatus(prev => prev === 'offline' ? 'ok' : prev);
    const handleServerDown = () => setStatus(prev => prev === 'offline' ? prev : 'serverDown');
    const handleServerUp = () => {
      // Only trigger recovery refetch if we were actually showing the server-down banner.
      // Otherwise this fires on every successful HTTP 200 (see axios interceptor),
      // creating an invalidate→refetch→invalidate infinite loop with ~90 req/s.
      setStatus(prev => {
        if (prev === 'serverDown') {
          // Coming back from server down → refresh stale data
          try {
            if (window.queryClient) window.queryClient.invalidateQueries();
          } catch {}
          return 'ok';
        }
        return prev;
      });
    };

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    window.addEventListener('serverDown', handleServerDown);
    window.addEventListener('serverUp', handleServerUp);

    if (!navigator.onLine) setStatus('offline');

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('serverDown', handleServerDown);
      window.removeEventListener('serverUp', handleServerUp);
    };
  }, []);

  // Auto-recovery: when server is down, poll health every 5s
  useEffect(() => {
    if (status !== 'serverDown') {
      if (recoveryRef.current) clearInterval(recoveryRef.current);
      return;
    }
    recoveryRef.current = setInterval(async () => {
      try {
        const res = await fetch('/api/health', { signal: AbortSignal.timeout(5000) });
        if (res.ok) {
          window.dispatchEvent(new Event('serverUp'));
        }
      } catch {}
    }, 5000);
    return () => clearInterval(recoveryRef.current);
  }, [status]);

  if (status === 'ok') return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-[300]" data-testid="offline-notice">
      <div className={`py-2 px-4 text-center text-xs font-semibold flex items-center justify-center gap-2 ${
        status === 'offline' ? 'bg-red-600/90 text-white' : 'bg-amber-600/90 text-white'
      }`}>
        {status === 'offline' ? (
          <><WifiOff className="w-3.5 h-3.5" /> Sei offline</>
        ) : (
          <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Server in aggiornamento</>
        )}
      </div>
    </div>
  );
};

export default OfflineNotice;
